import { StatusChipVariant } from '@components/widgets/StatusChip';
import { InspectionDetailStoreState } from '@stores';
import { InspectionStatusEnum, LenderDocumentTypeEnum } from '@types';

const MARINE_DOCUMENT_KEYS = [
  LenderDocumentTypeEnum.MARINE_FRONT,
  LenderDocumentTypeEnum.MARINE_BACK,
  LenderDocumentTypeEnum.MARINE_LEFT,
  LenderDocumentTypeEnum.MARINE_RIGHT,
  LenderDocumentTypeEnum.MARINE_HIN,
  LenderDocumentTypeEnum.MARINE_REGO_NUMBER
] as const;

const MARINE_EXTERNAL_PHOTOS_KEYS = [
  LenderDocumentTypeEnum.MARINE_FRONT,
  LenderDocumentTypeEnum.MARINE_BACK,
  LenderDocumentTypeEnum.MARINE_LEFT,
  LenderDocumentTypeEnum.MARINE_RIGHT,
] as const;

/**
 * Returns true if any marine document in the given documents map has needsResubmit.
 * When documentTypes is provided, only template-required marine docs are considered.
 */
export function hasMarineDocumentNeedsResubmit(
  documents: Record<string, { needsResubmit?: boolean } | undefined> | undefined,
  documentTypes?: string[]
): boolean {
  if (!documents) return false;
  return MARINE_DOCUMENT_KEYS.some(
    (key) =>
      (!documentTypes || documentTypes.includes(key)) &&
      documents[key]?.needsResubmit === true
  );
}

export interface MarineExternalSteps {
  marineFront: { isDone: boolean };
  marineBack: { isDone: boolean };
  marineLeft: { isDone: boolean };
  marineRight: { isDone: boolean };
}

/**
 * Returns true if any marine external photo needs resubmit.
 * When marineSteps is provided, also requires the step to be not done (!isDone && needsResubmit) per photo.
 */
export function hasMarineExternalPhotoNeedsResubmit(
  documents: Record<string, { needsResubmit?: boolean } | undefined> | undefined,
  marineSteps?: MarineExternalSteps
): boolean {
  if (!documents) return false;
  if (!marineSteps) {
    return MARINE_EXTERNAL_PHOTOS_KEYS.some(
      (key) => documents[key]?.needsResubmit === true
    );
  }
  const steps: { isDone: boolean }[] = [
    marineSteps.marineFront,
    marineSteps.marineBack,
    marineSteps.marineLeft,
    marineSteps.marineRight
  ];
  return MARINE_EXTERNAL_PHOTOS_KEYS.some(
    (key, index) => !steps[index].isDone && documents[key]?.needsResubmit === true
  );
}

/**
 * Check if the inspection status indicates it is blocked.
 * @param status - The inspection status string.
 * @returns True if status is blocked, false otherwise.
 */
export function isInspectionBlockedOrApprovalRequired(status: InspectionStatusEnum): boolean {
  return [
    InspectionStatusEnum.RULE_BLOCKED,
    InspectionStatusEnum.APPROVAL_REQUIRED
  ].includes(status);
}

export function shouldShowInspectionActionPanel(inspectionDetailData: InspectionDetailStoreState['inspectionDetailData']): boolean {
  return isInspectionBlockedOrApprovalRequired(inspectionDetailData.status) &&
    inspectionDetailData.inspectionChecks?.rules &&
    inspectionDetailData.inspectionChecks?.rules.length > 0;
}

export function getInspectionStatusChipVariant(inspectionDetailData: InspectionDetailStoreState['inspectionDetailData']): StatusChipVariant {
  return inspectionDetailData.status ===
    InspectionStatusEnum.RULE_BLOCKED
    ? 'error_light'
    : inspectionDetailData.status ===
      InspectionStatusEnum.Completed
      ? 'success1'
      : 'info2';
}

const STATE_TO_TIMEZONE: Record<string, string> = {
  NSW: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  WA: 'Australia/Perth',
  TAS: 'Australia/Hobart',
  ACT: 'Australia/Sydney',
  NT: 'Australia/Darwin'
};

/**
 * Maps Australian sale state code to IANA timezone (defaults to Sydney).
 */
export function getTimeZoneForState(state?: string): string {
  return (state && STATE_TO_TIMEZONE[state]) || 'Australia/Sydney';
}

/**
 * Current local time in the state’s timezone (24h HH:MM), derived from state.
 */
export function getLocalTimeStringForState(state?: string): string {
  const timeZone = getTimeZoneForState(state);
  return new Date().toLocaleTimeString('en-AU', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Whether the current time in the given state's timezone is within business hours.
 * Weekday: 9:00–18:00, Weekend: 10:00–16:00 (local time).
 */
export function isWithinBusinessHours(state?: string): boolean {
  const timeZone = getTimeZoneForState(state);
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-AU', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hours = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minutes = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const timeValue = hours + minutes / 60;

  const businessHours = {
    weekday: { start: 9, end: 18 },
    weekend: { start: 10, end: 16 }
  };

  const isWeekend = weekday === 'Sat' || weekday === 'Sun';
  const { start, end } = isWeekend ? businessHours.weekend : businessHours.weekday;
  return timeValue >= start && timeValue < end;
}
